/* oxlint-disable react/jsx-props-no-spreading */
import * as SelectPrimitive from '@radix-ui/react-select';
import {
  ComponentProps,
  ComponentPropsWithoutRef,
  ElementRef,
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import Icon from '~/components/Icon';
import { Simplify } from '~/utils/types';
import Field from './Field';
import {
  basePickerFieldStyles,
  basePickerLabelStyles,
  basePickerListboxStyles,
  basePickerOptionStyles,
} from './sharedFieldStyles';

const SelectContent = forwardRef<
  ElementRef<typeof SelectPrimitive.Content>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      {...props}
      ref={ref}
      className={twMerge(basePickerListboxStyles, className)}
      position={position}
      sideOffset={4}
      data-component="corso-combobox-listbox"
    >
      <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
        <Icon icon="ChevronUp" className="size-4" />
      </SelectPrimitive.ScrollUpButton>
      <SelectPrimitive.Viewport
        className={twMerge(
          'p-1',
          position === 'popper' &&
            'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
        <Icon icon="ChevronDown" className="size-4" />
      </SelectPrimitive.ScrollDownButton>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectItem = forwardRef<
  ElementRef<typeof SelectPrimitive.Item>,
  Simplify<
    ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
      addon?: ReactNode;
    }
  >
>(({ className, children, addon, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    {...props}
    className={twMerge(
      basePickerOptionStyles, // disabled
      'data-disabled:pointer-events-none data-disabled:text-on-surface/(--state-disabled-mix)',
      className,
    )}
    data-component="corso-combobox-option"
  >
    {addon}
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>

    <SelectPrimitive.ItemIndicator>
      <Icon icon="Check" />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectTrigger = forwardRef<
  ElementRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    {...props}
    className={twMerge(basePickerFieldStyles, className)}
    data-component="corso-combobox"
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <Icon
        icon="ChevronDown"
        className="absolute top-4 right-4 text-on-surface-variant transition-transform group-data-invalid:text-critical group-data-[state=open]:-rotate-180"
      />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

// only valueMissing is applicable for select
type ValidationConstraint = Extract<keyof ValidityState, 'valueMissing'>;

type SelectProps = Simplify<
  {
    id: string;
    name: string;
    label: string;
    description?: ReactNode;
    validationMessages?: Record<ValidationConstraint, string>;
    className?: string;
  } & Omit<ComponentProps<typeof SelectPrimitive.Root>, 'name' | 'id'>
>;

// TODO: add optional flag
function Select({
  id,
  name,
  label,
  value,
  children,
  required,
  className,
  description,
  defaultValue,
  onValueChange,
  validationMessages,
  ...props
}: SelectProps) {
  // need to control the select value to float the label
  const [fieldValue, setFieldValue] = useState<string | undefined>(
    value ?? defaultValue,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const selectRef = useRef<HTMLSelectElement>();

  useEffect(() => {
    if (!selectRef.current) {
      const sibling = triggerRef.current?.nextElementSibling;

      if (!(sibling instanceof HTMLSelectElement)) {
        throw new Error(
          'Select component must be the first child of the SelectRoot component',
        );
      }

      selectRef.current = sibling;
      // make sure the select is not focusable
      selectRef.current.setAttribute('tabindex', '-1');
    }
  });

  return (
    <Field.Root className={twMerge(className, 'relative')}>
      <SelectPrimitive.Root
        {...props}
        defaultValue={fieldValue}
        onValueChange={(selected) => {
          setFieldValue(selected);
          setIsValid(true);
          setErrorMessage(null);
          triggerRef.current?.setCustomValidity('');
          onValueChange?.(selected);
        }}
      >
        <SelectTrigger
          aria-required={required}
          aria-describedby={`${id}-error-text ${id}-help-text`}
          ref={triggerRef}
          id={id}
          data-invalid={!isValid ? '' : undefined}
          data-empty={fieldValue ? undefined : ''}
          className="peer"
        >
          <SelectPrimitive.Value />

          <Field.Label htmlFor={id} className={basePickerLabelStyles}>
            {label}
          </Field.Label>
        </SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </SelectPrimitive.Root>
      <input
        // type hidden will not participate in the validation
        aria-hidden
        // using hidden class causes a not focusable element error
        className="sr-only"
        tabIndex={-1}
        required={required}
        name={name}
        onChange={() => {}}
        value={fieldValue ?? ''}
        onInvalid={() => {
          setIsValid(false);
          setErrorMessage(
            validationMessages?.valueMissing ??
              'Please select an item from the list.',
          );
          triggerRef.current?.setCustomValidity(
            validationMessages?.valueMissing ?? '',
          );
        }}
      />

      {errorMessage && (
        <Field.ErrorText id={`${id}-error-text`}>
          {errorMessage}
        </Field.ErrorText>
      )}
      {description && !errorMessage && (
        <Field.HelpText
          id={`${id}-help-text`}
          className="text-on-surface-variant peer-disabled:text-on-surface/(--state-disabled-mix) peer-data-empty:peer-disabled:text-on-surface/(--state-disabled-mix)"
        >
          {description}
        </Field.HelpText>
      )}
    </Field.Root>
  );
}

export { Select, SelectItem };
